import React, { useEffect, useMemo, useRef, useState } from "react"
import { Tab, Tabs } from "react-bootstrap"
import { FieldErrors, FieldValues, get, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"
import { isEqual } from "lodash-es"

import { useControlledState } from "shared/lib/useControlledState"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamMemberList } from "entities/team-members/ui/team-member-list/TeamMemberList"
import { TeamFragment } from "entities/teams/graphql/fragment.generated"
import { ProjectFormData } from "features/project-management/types/ProjectFormData"
import { ProjectFormTabType } from "features/project-management/types/ProjectFormTabType"

import { useProjectFormContext } from "../contexts/ProjectFormContext"
import { ProjectFormClarificationRequest } from "./ProjectFormClarificationRequest"
import { ProjectFormDescription } from "./ProjectFormDescription"
import { ProjectFormImage } from "./ProjectFormImage"
import { ProjectFormInflationSurcharge } from "./ProjectFormInflationSurcharge"
import { ProjectFormPhase } from "./ProjectFormPhase"
import { ProjectFormPriceGroups } from "./ProjectFormPriceGroups"
import { ProjectFormPriceList } from "./ProjectFormPriceList"
import { ProjectFormPriceTotal } from "./ProjectFormPriceTotal"

const TAB_FIELD_MAPPING: Record<ProjectFormTabType, string[]> = {
  [ProjectFormTabType.GENERAL]: [
    "project.title",
    "project.phase",
    "project.ui.color"
  ],
  [ProjectFormTabType.TEAM]: [],
  [ProjectFormTabType.PRICES]: [
    "project.priceList",
    "project.inflationSurcharge"
  ],
  [ProjectFormTabType.NOTES]: ["project.description"],
  [ProjectFormTabType.CLARIFICATION_REQUEST]: ["clarificationRequest.content"]
}

function useTabErrors() {
  const { subscribe, formState } = useFormContext()
  const initialErrors =
    formState.errors && Object.keys(formState.errors).length > 0
      ? formState.errors
      : null
  const [errors, setErrors] = useState<FieldErrors<FieldValues> | null>(
    initialErrors
  )
  const errorsRef = useRef(errors)

  useEffect(() => {
    return subscribe({
      formState: { errors: true },
      callback: state => {
        const next =
          state.errors && Object.keys(state.errors).length > 0
            ? { ...state.errors }
            : null
        if (!isEqual(next, errorsRef.current)) {
          errorsRef.current = next
          setErrors(next)
        }
      }
    })
  }, [subscribe])

  const tabErrors = useMemo(() => {
    const result: Record<ProjectFormTabType, boolean> = {
      [ProjectFormTabType.GENERAL]: false,
      [ProjectFormTabType.TEAM]: false,
      [ProjectFormTabType.PRICES]: false,
      [ProjectFormTabType.NOTES]: false,
      [ProjectFormTabType.CLARIFICATION_REQUEST]: false
    }
    Object.entries(TAB_FIELD_MAPPING).forEach(([tab, fields]) => {
      result[tab as ProjectFormTabType] = fields.some(
        fieldPath => !!get(errors, fieldPath)
      )
    })
    return result
  }, [errors])

  return tabErrors
}

interface TabTitleProps {
  hasError: boolean
  children: React.ReactNode
}

const TabTitle = React.memo(({ hasError, children }: TabTitleProps) => (
  <span
    className={classNames("project-form-tab__title", {
      "nav-link__content--error": hasError,
      "project-form-tab__title--error": hasError
    })}
  >
    {hasError ? (
      <Icon
        name={IconName.WARNING}
        size={IconSize.SMALL}
        className="project-form-tab__error-icon"
      />
    ) : null}
    {children}
  </span>
))

const GeneralTab = React.memo(() => {
  return (
    <div className="project-form__tab-content">
      <ProjectFormImage />
      <ProjectFormPhase />
    </div>
  )
})

interface TeamTabProps {
  team: TeamFragment | null | undefined
  onManageProjectTeam?: () => void
}

const TeamTab = React.memo(({ team, onManageProjectTeam }: TeamTabProps) => {
  const { t } = useTranslation()
  const memberships = team?.memberships

  if (!memberships?.length) {
    return null
  }

  return (
    <div className="project-form__tab-content project-form__team-list">
      <TeamMemberList teamMembers={team?.memberships} isReadonly />
      {onManageProjectTeam ? (
        <div className="project-form__team-list-footer">
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={onManageProjectTeam}
            className="project-team-popover__manage-button"
            data-testid="project-team-popover__manage-button"
          >
            {t("ProjectTeam.Actions.Edit")}
          </Button>
        </div>
      ) : null}
    </div>
  )
})

const PricesTab = React.memo(() => {
  return (
    <div className="project-form__tab-content">
      <ProjectFormPriceTotal />
      <ProjectFormPriceGroups />
      <ProjectFormInflationSurcharge />
      <ProjectFormPriceList />
    </div>
  )
})

const NotesTab = React.memo(() => {
  return (
    <div className="project-form__tab-content">
      <ProjectFormDescription hideLabel />
    </div>
  )
})

interface ClarificationRequestTabTitleProps {
  hasError: boolean
  hasClarificationRequest: boolean
}

const ClarificationRequestTabTitle = React.memo(
  ({
    hasError,
    hasClarificationRequest
  }: ClarificationRequestTabTitleProps) => {
    const { t } = useTranslation()
    return (
      <span
        className={classNames("project-form-tab__title", {
          "nav-link__content--warning": hasClarificationRequest && !hasError,
          "nav-link__content--error": hasError,
          "project-form-tab__title--error": hasError
        })}
      >
        {hasError ? (
          <Icon
            name={IconName.WARNING}
            size={IconSize.SMALL}
            className="project-form-tab__error-icon"
          />
        ) : null}
        {!hasError && hasClarificationRequest ? (
          <Icon name={IconName.CLARIFICATION_REQUEST} size={IconSize.SMALL} />
        ) : null}
        {t("ClarificationRequest.Form.Tab.Label")}
      </span>
    )
  }
)

const ClarificationRequestTab = React.memo(() => {
  return (
    <div className="project-form__tab-content">
      <ProjectFormClarificationRequest />
    </div>
  )
})

export interface ProjectFormTabsProps extends WithClassName {
  activeTab?: ProjectFormTabType
  onTabChange?: (tab: ProjectFormTabType) => void
  defaultTab?: ProjectFormTabType
}

export const ProjectFormTabs = React.memo(
  ({ className, activeTab, onTabChange, defaultTab }: ProjectFormTabsProps) => {
    const { t } = useTranslation()
    const { isReadonly, model } = useProjectFormContext()
    const team = model?.team.data.team?.data
    const onManageTeam = model?.team.handlers.onManageTeam ?? undefined
    const { watch } = useFormContext<ProjectFormData>()
    const description = watch("project.description")
    const tabErrors = useTabErrors()

    const hasClarificationRequest =
      !!model?.core.data.project?.data?.hasClarificationRequest

    const showTeamTab = !!team
    const showRemarksTab = !isReadonly || !!description
    const showClarificationRequestTab = !isReadonly || hasClarificationRequest

    const [effectiveTab, handleTabChange] =
      useControlledState<ProjectFormTabType>({
        value: activeTab,
        defaultValue: defaultTab ?? ProjectFormTabType.GENERAL,
        onChange: onTabChange
      })

    useEffect(() => {
      if (effectiveTab === ProjectFormTabType.TEAM && !showTeamTab)
        handleTabChange(ProjectFormTabType.GENERAL)
      else if (effectiveTab === ProjectFormTabType.NOTES && !showRemarksTab)
        handleTabChange(ProjectFormTabType.GENERAL)
      else if (
        effectiveTab === ProjectFormTabType.CLARIFICATION_REQUEST &&
        !showClarificationRequestTab
      )
        handleTabChange(ProjectFormTabType.GENERAL)
    }, [effectiveTab, showTeamTab, showRemarksTab, showClarificationRequestTab])

    return (
      <Tabs
        activeKey={effectiveTab}
        onSelect={key =>
          handleTabChange(
            (key as ProjectFormTabType) ?? ProjectFormTabType.PRICES
          )
        }
        className={className ?? "project-form__tabs"}
      >
        <Tab
          eventKey={ProjectFormTabType.GENERAL}
          title={
            <TabTitle hasError={tabErrors[ProjectFormTabType.GENERAL]}>
              {t("General.General")}
            </TabTitle>
          }
        >
          <GeneralTab />
        </Tab>

        {team ? (
          <Tab
            eventKey={ProjectFormTabType.TEAM}
            title={
              <TabTitle hasError={tabErrors[ProjectFormTabType.TEAM]}>
                {t("Project.Team.Title")}
              </TabTitle>
            }
          >
            <TeamTab team={team} onManageProjectTeam={onManageTeam} />
          </Tab>
        ) : null}

        <Tab
          eventKey={ProjectFormTabType.PRICES}
          title={
            <TabTitle hasError={tabErrors[ProjectFormTabType.PRICES]}>
              {t("Price.Prices")}
            </TabTitle>
          }
        >
          <PricesTab />
        </Tab>

        {showRemarksTab ? (
          <Tab
            eventKey={ProjectFormTabType.NOTES}
            title={
              <TabTitle hasError={tabErrors[ProjectFormTabType.NOTES]}>
                {t("General.Remark")}
              </TabTitle>
            }
          >
            <NotesTab />
          </Tab>
        ) : null}

        {showClarificationRequestTab ? (
          <Tab
            eventKey={ProjectFormTabType.CLARIFICATION_REQUEST}
            title={
              <ClarificationRequestTabTitle
                hasError={tabErrors[ProjectFormTabType.CLARIFICATION_REQUEST]}
                hasClarificationRequest={hasClarificationRequest}
              />
            }
          >
            <ClarificationRequestTab />
          </Tab>
        ) : null}
      </Tabs>
    )
  }
)

ProjectFormTabs.displayName = "ProjectFormTabs"
